/**
 * Réglages du projet : colonnes kanban, étiquettes, partage, membres, archivage, suppression.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert, appConfirm, appPrompt } from '../dialogs/service.js';
import {
  Eyebrow,
  LoadError,
  Panel,
  TruncatedListWarning,
  labelForStatus,
} from '../components/common.jsx';
import { TASK_STATUSES } from '@exiplan/shared-types';
import { BTN_DARK_SM, BTN_GHOST_SM, SELECT_SM } from '../ui/theme.js';
import { DeleteProjectDialog } from '../dialogs/DeleteProjectDialog.jsx';
import { MoveToOrganizationSettings } from './MoveToOrganizationSettings.jsx';

function ColumnsSettings({ projectId }) {
  const { t } = useTranslation();
  const {
    data: columns,
    error: loadError,
    reload,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}/columns`, { signal }).then((r) => r.data ?? []),
    [projectId],
  );

  async function addColumn() {
    const name = await appPrompt(t('settings.columns.addPrompt'));
    if (!name) return;
    try {
      await api.post(`/projects/${projectId}/columns`, { name });
      reload();
    } catch (error) {
      await appAlert(t('settings.columns.createFailed', { message: error.message }));
    }
  }

  /**
   * Le statut qu'applique la colonne. La chaîne vide du `<select>` est renvoyée en `null` —
   * `''` n'est pas une valeur du contrat, et `columnUpdateSchema` la refuserait ; surtout,
   * « ne pilote pas le statut » est une intention, pas un champ resté vide.
   */
  async function setColumnStatus(columnId, value) {
    try {
      await api.patch(`/projects/${projectId}/columns/${columnId}`, { status: value || null });
      reload();
    } catch (error) {
      await appAlert(t('settings.columns.statusFailed', { message: error.message }));
    }
  }

  async function removeColumn(columnId) {
    if (!(await appConfirm(t('settings.columns.removeConfirm')))) return;
    try {
      await api.delete(`/projects/${projectId}/columns/${columnId}`);
      reload();
    } catch (error) {
      await appAlert(t('settings.columns.deleteFailed', { message: error.message }));
    }
  }

  return (
    <Panel>
      <div className="flex items-center justify-between">
        <Eyebrow>{t('settings.columns.title')}</Eyebrow>
        <button type="button" className={BTN_GHOST_SM} onClick={addColumn}>
          {t('settings.columns.add')}
        </button>
      </div>
      {loadError && <LoadError error={loadError} onRetry={reload} className="mt-3" />}
      <ul className="mt-3 flex flex-col gap-2">
        {(columns ?? []).map((column) => (
          <li
            key={column.id}
            className="flex items-center justify-between border-2 border-line-soft p-2 text-sm"
          >
            <span>
              {column.name}
              {column.wipLimit
                ? t('settings.columns.wipLimitSuffix', { limit: column.wipLimit })
                : ''}
            </span>
            <div className="flex items-center gap-2">
              {/* Le libellé nomme la colonne : « Statut appliqué » seul se répète à
                  l'identique sur chaque ligne et ne dit rien au lecteur d'écran. */}
              <select
                aria-label={t('settings.columns.statusAria', { column: column.name })}
                className={SELECT_SM}
                value={column.status ?? ''}
                onChange={(e) => setColumnStatus(column.id, e.target.value)}
              >
                <option value="">{t('settings.columns.statusNone')}</option>
                {TASK_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {labelForStatus(status, t)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={BTN_GHOST_SM}
                onClick={() => removeColumn(column.id)}
              >
                {t('settings.columns.remove')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function LabelsSettings({ projectId }) {
  const { t } = useTranslation();
  const {
    data: labels,
    error: loadError,
    reload,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}/labels`, { signal }).then((r) => r.data ?? []),
    [projectId],
  );

  async function addLabel() {
    const name = await appPrompt(t('settings.labels.addPrompt'));
    if (!name) return;
    try {
      await api.post(`/projects/${projectId}/labels`, { name });
      reload();
    } catch (error) {
      await appAlert(t('settings.labels.createFailed', { message: error.message }));
    }
  }

  async function removeLabel(labelId) {
    if (!(await appConfirm(t('settings.labels.removeConfirm')))) return;
    try {
      await api.delete(`/projects/${projectId}/labels/${labelId}`);
      reload();
    } catch (error) {
      await appAlert(t('settings.labels.deleteFailed', { message: error.message }));
    }
  }

  return (
    <Panel>
      <div className="flex items-center justify-between">
        <Eyebrow>{t('settings.labels.title')}</Eyebrow>
        <button type="button" className={BTN_GHOST_SM} onClick={addLabel}>
          {t('settings.labels.add')}
        </button>
      </div>
      {loadError && <LoadError error={loadError} onRetry={reload} className="mt-3" />}
      <div className="mt-3 flex flex-wrap gap-2">
        {(labels ?? []).map((label) => (
          <span
            key={label.id}
            className="flex items-center gap-1.5 border-2 border-line px-2 py-1 text-xs"
          >
            {label.name}
            <button
              type="button"
              aria-label={t('settings.labels.removeAria', { name: label.name })}
              onClick={() => removeLabel(label.id)}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    </Panel>
  );
}

function SharesSettings({ projectId, organizationKind }) {
  const { t } = useTranslation();
  const {
    data: sharesPage,
    error: loadError,
    reload,
  } = useAsync((signal) => fetchAllPages(`/projects/${projectId}/shares`, { signal }), [projectId]);
  const shares = sharesPage?.data;
  const [scope, setScope] = useState('read');
  // Un projet de l'espace personnel n'a pas d'invitation (ORG-2) : son partage public reste en
  // lecture seule, symétrique du refus serveur `PERSONAL_SPACE_READ_ONLY` sur `scope: 'comment'`.
  const isPersonal = organizationKind === 'personnel';

  async function createShare() {
    try {
      const { data } = await api.post(`/projects/${projectId}/shares`, { scope });
      // Le jeton complet n'est renvoyé qu'ici : la liste ne montre qu'un aperçu, pour qu'un
      // lien copié par un membre ne reste pas récupérable après son départ de l'équipe.
      const url = `${window.location.origin}/partage/${data.token}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      await appAlert(t('settings.shares.createdMessage', { url }), {
        title: t('settings.shares.createdTitle'),
      });
      reload();
    } catch (error) {
      await appAlert(t('settings.shares.createFailed', { message: error.message }));
    }
  }

  async function revokeShare(shareId) {
    if (!(await appConfirm(t('settings.shares.revokeConfirm')))) return;
    try {
      await api.delete(`/projects/${projectId}/shares/${shareId}`);
      reload();
    } catch (error) {
      await appAlert(t('settings.shares.revokeFailed', { message: error.message }));
    }
  }

  return (
    <Panel>
      <Eyebrow>{t('settings.shares.title')}</Eyebrow>
      <div className="mt-3 flex items-center gap-2">
        <select
          className={SELECT_SM}
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          aria-label={t('settings.shares.scopeAria')}
        >
          <option value="read">{t('settings.shares.read')}</option>
          {!isPersonal && <option value="comment">{t('settings.shares.comment')}</option>}
        </select>
        <button type="button" className={BTN_GHOST_SM} onClick={createShare}>
          {t('settings.shares.create')}
        </button>
      </div>
      {loadError && <LoadError error={loadError} onRetry={reload} className="mt-3" />}
      {sharesPage?.truncated && (
        <div className="mt-3">
          <TruncatedListWarning />
        </div>
      )}
      <ul className="mt-3 flex flex-col gap-2">
        {(shares ?? []).map((share) => (
          <li
            key={share.id}
            className="flex items-center justify-between border-2 border-line-soft p-2 text-xs"
          >
            <span className="font-mono">
              /partage/{share.tokenPreview} — {share.scope}
              {share.revokedAt ? t('settings.shares.revoked') : ''}
            </span>
            <button type="button" className={BTN_GHOST_SM} onClick={() => revokeShare(share.id)}>
              {t('settings.shares.revoke')}
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ArchiveSettings({ projectId }) {
  const { t } = useTranslation();

  async function archive() {
    if (!(await appConfirm(t('settings.archive.confirm')))) return;
    try {
      await api.patch(`/projects/${projectId}`, { status: 'archived' });
    } catch (error) {
      await appAlert(t('settings.archive.failed', { message: error.message }));
    }
  }

  return (
    <Panel>
      <Eyebrow>{t('settings.archive.title')}</Eyebrow>
      <p className="mt-2 text-sm text-ink-soft">{t('settings.archive.description')}</p>
      <button type="button" className={`${BTN_GHOST_SM} mt-3`} onClick={archive}>
        {t('settings.archive.action')}
      </button>
    </Panel>
  );
}

/**
 * Zone de danger : suppression définitive. Proposée seulement si le serveur accorde le droit
 * (`canDelete` de `GET /projects/:id`) — il revérifie rôle `admin` et droit de suppression.
 */
function DeleteProjectSettings({ projectId, projectName, onDeleted }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Panel>
      <Eyebrow>{t('settings.delete.title')}</Eyebrow>
      <p className="mt-2 text-sm text-ink-soft">{t('settings.delete.description')}</p>
      <button type="button" className={`${BTN_DARK_SM} mt-3`} onClick={() => setOpen(true)}>
        {t('settings.delete.action')}
      </button>
      {open && (
        <DeleteProjectDialog
          projectId={projectId}
          projectName={projectName}
          onClose={() => setOpen(false)}
          onDeleted={onDeleted}
        />
      )}
    </Panel>
  );
}

export function SettingsView({
  projectId,
  projectKey,
  projectName,
  organizationKind,
  projectStatus,
  canDelete,
  onTransferred,
  onDeleted,
}) {
  const isPersonal = organizationKind === 'personnel';
  // M7 : un projet archivé est en lecture seule — le transfert n'est pas proposé.
  const canTransfer = isPersonal && projectStatus !== 'archived';
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <ColumnsSettings projectId={projectId} />
      <LabelsSettings projectId={projectId} />
      <SharesSettings projectId={projectId} organizationKind={organizationKind} />
      <ArchiveSettings projectId={projectId} />
      {canTransfer && (
        <MoveToOrganizationSettings
          projectId={projectId}
          projectKey={projectKey}
          onTransferred={onTransferred}
        />
      )}
      {canDelete && projectName && (
        <DeleteProjectSettings
          projectId={projectId}
          projectName={projectName}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
